import Api, { Company, CompanyPauseTeam, WorkHoursDay } from "@/api";
import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as React from "react";
import { useParams } from "react-router-dom";

import { PrimaryButton, showErrorToast, showSuccessToast } from "turboui";

import { PageNavigation } from "@/features/accounts/PageNavigation";
import { PageModule } from "@/routes/types";

export default { name: "AccountWorkHoursPage", loader, Page } as PageModule;

interface LoaderResult {
  companies: Company[];
}

async function loader(): Promise<LoaderResult> {
  return {
    companies: await Api.companies.list({}).then((res) => res.companies!),
  };
}

const DAYS: { key: string; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

// A small curated timezone list; America/Los_Angeles is the default.
const TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Bucharest",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

const DEFAULT_TIMEZONE = "America/Los_Angeles";

function defaultDays(): WorkHoursDay[] {
  return DAYS.map((d) => ({
    day: d.key,
    enabled: d.key !== "sat" && d.key !== "sun",
    startMinute: 540, // 09:00
    endMinute: 1020, // 17:00
  }));
}

// Merge whatever the backend returned into a full, ordered 7-day list.
function normalizeDays(workHours?: WorkHoursDay[] | null): WorkHoursDay[] {
  const base = defaultDays();
  if (!workHours || workHours.length === 0) return base;

  return base.map((d) => {
    const found = workHours.find((w) => w.day === d.day);
    return found ? { ...d, ...found } : d;
  });
}

// Plain-English list: "Engineering", "Engineering and Support", "A, B and C".
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function Page() {
  const { companies } = Pages.useLoadedData<LoaderResult>();

  return (
    <Pages.Page title={["Work Hours", "Account"]} testId="account-work-hours-page">
      <Paper.Root size="small">
        <PageNavigation />
        <Paper.Body>
          <div className="mb-2 text-content-accent text-2xl font-bold">Work hours</div>
          <p className="mb-6 text-sm text-content-dimmed">
            Set each company's business hours and timezone. Recurring tasks with{" "}
            <span className="font-medium">"Only run during work hours"</span> checked will only fire inside these hours.
          </p>

          {companies.length === 0 ? (
            <p className="text-content-dimmed">You don't belong to any companies yet.</p>
          ) : (
            <Form companies={companies} />
          )}
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function Form({ companies }: { companies: Company[] }) {
  // Default the selector to the company in the URL (/:companyId/...), so the page
  // shows the company you navigated from and a refresh keeps you on it — instead of
  // silently resetting to companies[0] and editing the wrong company.
  const { companyId: urlCompanyId } = useParams();
  const initialId = companies.find((c) => c.id === urlCompanyId)?.id ?? companies[0]!.id;

  const [companyId, setCompanyId] = React.useState<string>(initialId);
  const selected = companies.find((c) => c.id === companyId) ?? companies[0]!;

  const [timezone, setTimezone] = React.useState<string>(selected.timezone || DEFAULT_TIMEZONE);
  const [days, setDays] = React.useState<WorkHoursDay[]>(normalizeDays(selected.workHours));
  const [saving, setSaving] = React.useState(false);

  // SECRET PAUSE: separate from work hours. Pausing puts the company's AI workforce to sleep
  // WITHOUT touching the stored schedule below (which is why we never clear `days`/`timezone`
  // when paused — they stay exactly as set and resume on their own the moment you switch it
  // back on). The pause can hold individual TEAMS open; those keep working through it.
  const [paused, setPaused] = React.useState<boolean>(!!selected.paused);
  const [pausing, setPausing] = React.useState(false);

  const [teams, setTeams] = React.useState<CompanyPauseTeam[]>([]);
  const [teamsLoading, setTeamsLoading] = React.useState(true);
  const [savingTeamId, setSavingTeamId] = React.useState<string | null>(null);

  const exemptTeams = teams.filter((t) => t.exempt);

  // THE RULE: work hours are meaningless only when NOBODY is awake. A pause with at least one
  // team held open still governs that team's day, so the editor comes back to life.
  const fullyAsleep = paused && exemptTeams.length === 0;

  // Reseed the editor whenever the selected company changes.
  React.useEffect(() => {
    setTimezone(selected.timezone || DEFAULT_TIMEZONE);
    setDays(normalizeDays(selected.workHours));
    setPaused(!!selected.paused);
  }, [companyId]);

  // Teams (and their exception state) live behind their own query so the company selector can
  // reach every company the account belongs to, not just the one in the URL.
  React.useEffect(() => {
    let cancelled = false;
    setTeamsLoading(true);

    Api.getCompanyPauseTeams({ companyId })
      .then((res) => {
        if (!cancelled) setTeams(res.teams ?? []);
      })
      .catch(() => {
        if (!cancelled) setTeams([]);
      })
      .finally(() => {
        if (!cancelled) setTeamsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const togglePause = async () => {
    const next = !paused;
    setPausing(true);
    try {
      await Api.setCompanyPaused({ companyId, paused: next });
      setPaused(next);
      if (next) {
        const kept = exemptTeams.length;
        showSuccessToast(
          "Paused",
          kept === 0
            ? "All AI employees are asleep. Your work hours are preserved."
            : `Asleep except ${joinNames(exemptTeams.map((t) => t.name))}. Your work hours are preserved.`,
        );
      } else {
        showSuccessToast("Resumed", "Back to normal — agents pick up where they left off.");
      }
    } catch {
      showErrorToast("Error", "Failed to update pause state");
    } finally {
      setPausing(false);
    }
  };

  // Send the FULL set of teams that should stay awake — the mutation replaces the list, so a
  // partial send would silently put the others back to sleep.
  const toggleTeam = async (team: CompanyPauseTeam) => {
    const next = teams.map((t) => (t.id === team.id ? { ...t, exempt: !t.exempt } : t));
    const previous = teams;

    setTeams(next);
    setSavingTeamId(team.id);

    try {
      await Api.setCompanyPauseExceptions({
        companyId,
        spaceIds: next.filter((t) => t.exempt).map((t) => t.id),
      });
    } catch {
      setTeams(previous);
      showErrorToast("Error", `Failed to update ${team.name}`);
    } finally {
      setSavingTeamId(null);
    }
  };

  const updateDay = (key: string, patch: Partial<WorkHoursDay>) => {
    setDays((prev) => prev.map((d) => (d.day === key ? { ...d, ...patch } : d)));
  };

  // Copy one day's hours to every other day that is turned on (closed days stay closed).
  const copyToAll = (source: WorkHoursDay) => {
    setDays((prev) =>
      prev.map((d) => (d.enabled ? { ...d, startMinute: source.startMinute, endMinute: source.endMinute } : d)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Api.editCompanyWorkHours({ companyId, timezone, workHours: days });
      showSuccessToast("Saved", "Work hours updated");
    } catch {
      showErrorToast("Error", "Failed to save work hours");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-content-base mb-1">Company</label>
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="w-full rounded-md border border-surface-outline bg-surface-base px-3 py-2"
          data-test-id="work-hours-company"
        >
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <PauseCard
        paused={paused}
        pausing={pausing}
        onToggle={togglePause}
        teams={teams}
        exemptTeams={exemptTeams}
      />

      {paused && (
        <TeamExceptions
          teams={teams}
          loading={teamsLoading}
          savingTeamId={savingTeamId}
          onToggle={toggleTeam}
        />
      )}

      {/* The hours editor is grayed + inert only when the pause covers EVERYONE: the schedule is
          preserved untouched, it just isn't editable (or in effect) until something is awake to
          keep it. Hold one team open and the hours govern that team, so the editor returns. */}
      <div
        className={fullyAsleep ? "opacity-40 pointer-events-none select-none" : ""}
        aria-hidden={fullyAsleep}
        data-test-id="work-hours-editor"
      >
        <div>
          <label className="block text-sm font-medium text-content-base mb-1">Timezone</label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full rounded-md border border-surface-outline bg-surface-base px-3 py-2"
            data-test-id="work-hours-timezone"
            disabled={fullyAsleep}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2 mt-6">
          <label className="block text-sm font-medium text-content-base">Hours per day</label>
          {paused && exemptTeams.length > 0 && (
            <p className="text-xs text-content-dimmed" data-test-id="work-hours-scope-note">
              These hours govern {joinNames(exemptTeams.map((t) => t.name))} while the rest of the company sleeps.
            </p>
          )}
          {days.map((d) => (
            <DayRow
              key={d.day}
              day={d}
              onChange={(patch) => updateDay(d.day, patch)}
              onCopyToAll={() => copyToAll(d)}
            />
          ))}
        </div>

        <div className="flex justify-end mt-6">
          <PrimaryButton onClick={handleSave} loading={saving} testId="save-work-hours">
            Save changes
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// The secret pause switch. Big, unambiguous status + a single toggle. Deliberately quiet copy —
// this is the owner's private control, not something the team needs to see or know about.
function PauseCard({
  paused,
  pausing,
  onToggle,
  teams,
  exemptTeams,
}: {
  paused: boolean;
  pausing: boolean;
  onToggle: () => void;
  teams: CompanyPauseTeam[];
  exemptTeams: CompanyPauseTeam[];
}) {
  const partial = paused && exemptTeams.length > 0;
  const names = joinNames(exemptTeams.map((t) => t.name));

  const status = !paused
    ? "Running"
    : partial
      ? `Paused — ${exemptTeams.length} of ${teams.length} teams still working`
      : "Paused — AI workforce asleep";

  const body = !paused ? (
    exemptTeams.length > 0 ? (
      // The exception list survives a resume, so say out loud what a future pause will do.
      // Otherwise the setup is invisible while running and ambushes you months later.
      <>
        Pausing puts every AI employee in this company to sleep without touching the work hours below. When you pause:{" "}
        <span className="font-medium">{names}</span> keep working.
      </>
    ) : (
      <>
        Pausing puts every AI employee in this company to sleep without touching the work hours below. Nothing
        accumulates, so turning it back on won't cause a flood.
      </>
    )
  ) : partial ? (
    <>
      <span className="font-medium">{names}</span> keep working on the hours below. Every other team is asleep and will
      not answer, so anything handed to them waits until you resume. Nothing is lost.
    </>
  ) : (
    <>
      No agent wakes and nothing piles up. Your work hours below are preserved exactly as set. Switch back on and
      everything resumes on its own.
    </>
  );

  return (
    <div
      className={
        "rounded-lg border p-4 " +
        (paused ? "border-amber-400 bg-amber-50 dark:bg-amber-950/30" : "border-surface-outline bg-surface-base")
      }
      data-test-id="company-pause-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={
                "inline-block h-2.5 w-2.5 rounded-full " +
                (!paused ? "bg-green-500" : partial ? "bg-blue-500" : "bg-amber-500")
              }
            />
            <span className="text-sm font-semibold text-content-accent" data-test-id="company-pause-status">
              {status}
            </span>
          </div>
          <p className="mt-1 text-xs text-content-dimmed max-w-md">{body}</p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={pausing}
          data-test-id="company-pause-toggle"
          className={
            "shrink-0 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60 " +
            (paused ? "bg-green-600 hover:bg-green-700" : "bg-amber-600 hover:bg-amber-700")
          }
        >
          {pausing ? "…" : paused ? "Resume company" : "Pause company"}
        </button>
      </div>
    </div>
  );
}

// Per-team switches, shown only while paused. Turning one on wakes that team immediately; the
// rest of the company stays asleep.
function TeamExceptions({
  teams,
  loading,
  savingTeamId,
  onToggle,
}: {
  teams: CompanyPauseTeam[];
  loading: boolean;
  savingTeamId: string | null;
  onToggle: (team: CompanyPauseTeam) => void;
}) {
  return (
    <div className="rounded-lg border border-surface-outline bg-surface-base p-4" data-test-id="pause-exceptions-card">
      <div className="text-sm font-semibold text-content-accent">Teams that keep working</div>
      <p className="mt-1 mb-3 text-xs text-content-dimmed max-w-md">
        Switch a team back on and it works its normal hours while the rest of the company stays asleep. Sleeping teams
        will not answer it, so keep on whoever the work actually depends on.
      </p>

      {loading ? (
        <p className="text-sm text-content-dimmed">Loading teams…</p>
      ) : teams.length === 0 ? (
        <p className="text-sm text-content-dimmed">This company has no teams yet.</p>
      ) : (
        <div className="space-y-1">
          {teams.map((team) => (
            <label
              key={team.id}
              className="flex items-center gap-3 py-1 cursor-pointer"
              data-test-id={`pause-exception-${team.id}`}
            >
              <input
                type="checkbox"
                checked={team.exempt}
                disabled={savingTeamId === team.id}
                onChange={() => onToggle(team)}
              />
              <span className="text-sm">{team.name}</span>
              <span className="text-xs text-content-dimmed">
                {team.agentCount === 1 ? "1 AI employee" : `${team.agentCount} AI employees`}
              </span>
              {team.exempt && <span className="text-xs font-medium text-blue-600 dark:text-blue-400">working</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DayRow({
  day,
  onChange,
  onCopyToAll,
}: {
  day: WorkHoursDay;
  onChange: (patch: Partial<WorkHoursDay>) => void;
  onCopyToAll: () => void;
}) {
  const label = DAYS.find((d) => d.key === day.day)?.label ?? day.day;

  return (
    <div className="flex items-center gap-3 py-1" data-test-id={`work-hours-day-${day.day}`}>
      <label className="flex items-center gap-2 w-40 cursor-pointer">
        <input type="checkbox" checked={day.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} />
        <span className="text-sm">{label}</span>
      </label>

      {day.enabled ? (
        <div className="flex items-center gap-2 text-sm">
          <TimeInput value={day.startMinute} onChange={(m) => onChange({ startMinute: m })} />
          <span className="text-content-dimmed">to</span>
          <TimeInput value={day.endMinute} onChange={(m) => onChange({ endMinute: m })} />
          <button
            type="button"
            onClick={onCopyToAll}
            className="ml-1 text-xs text-content-dimmed hover:text-content-accent underline"
            title="Copy these hours to every day that is turned on"
            data-test-id={`work-hours-copy-${day.day}`}
          >
            Copy to all
          </button>
        </div>
      ) : (
        <span className="text-sm text-content-dimmed">Closed</span>
      )}
    </div>
  );
}

// Minutes-from-midnight <-> "HH:MM" for a native time input.
function TimeInput({ value, onChange }: { value: number; onChange: (minutes: number) => void }) {
  const hh = String(Math.floor(value / 60)).padStart(2, "0");
  const mm = String(value % 60).padStart(2, "0");

  return (
    <input
      type="time"
      value={`${hh}:${mm}`}
      onChange={(e) => {
        const [h, m] = e.target.value.split(":").map(Number);
        if (Number.isFinite(h) && Number.isFinite(m)) onChange(h * 60 + m);
      }}
      className="rounded-md border border-surface-outline bg-surface-base px-2 py-1"
    />
  );
}
